import { useLayoutEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { syncSessionReplay } from "../../utils/session-replay";
import { syncReplayDocument } from "../../utils/replay-document";

/*
 * Mount once inside <Router>. Re-evaluates the replay gate on every navigation —
 * see src/utils/session-replay.js for why the gate has to run on each route
 * change and not just at bootstrap.
 *
 * It does two things, in this order:
 *   1. per-route body suppression (syncSessionReplay), as before;
 *   2. the recording document (syncReplayDocument, M3): notes every router
 *      address (one that fails the address rule taints the document for good),
 *      and on a clean document where recording may run (built in, not turned
 *      off, no GPC, not the native app, no pending Google return) fences the
 *      page and only then starts Hotjar. It needs the ROUTER's own history
 *      object, because the fence turns that object's moves off the journey
 *      into full page loads.
 *
 * useLayoutEffect, NOT useEffect, and this is the whole control.
 *
 * React commits the new route's DOM and then schedules passive effects on a
 * MACROtask. A session recorder serializes incremental DOM from a
 * MutationObserver, whose callbacks drain at the MICROtask checkpoint — which
 * always runs first. With useEffect the exam page's first paint (stem, options)
 * is therefore serialized while <body> is still unsuppressed, every single
 * time. It is ordering, not a race, and it was measured: the stem reached a
 * modelled recorder under useEffect and did not under useLayoutEffect.
 *
 * Layout effects run synchronously after the commit and before the browser
 * yields, which is ahead of that checkpoint. The gate must close in the same
 * task that opened the page. The same timing fences a recording page before any
 * passive effect (attribution's address clean-up included) can run.
 */
const SessionReplayGate = () => {
  const history = useHistory();
  const { pathname, search, hash } = useLocation();
  useLayoutEffect(() => {
    // hash too: the module examines fragments, and dropping it here made that
    // branch dead code in production. Hotjar records fragments, and an implicit
    // -flow `#access_token=` would be invisible to a gate that never sees one.
    const address = `${pathname}${search || ""}${hash || ""}`;
    syncSessionReplay(address);
    syncReplayDocument(address, history);
    // `history` is the router's one object for the life of <Router>.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, hash]);
  return null;
};

export default SessionReplayGate;
